import { FundCategory } from '@/api/fund/queries';
import { Country } from '@/api/lookup/types';
import { EMAIL_REGEX } from '@/constants/static';
import { Feeder, StepForms } from '@/stores/slices/setup-fund-slice';
import { isValidHttpUrl } from '@/utils/http-url';

import { FundStructure } from '../constants/fund-structure';
import { isFeederComplete } from './feeder-helpers';
import { isFeederUsApplicable, isFundUsApplicable } from './is-us-applicable';
import { validateShareClasses, validateSubscriptionOpts } from './validate-fund-fields';

export { isValidHttpUrl };

export const isJurisdictionRegionRequired = (
  jurisdictionCountry: string | null | undefined,
  countries?: Country[],
): boolean => {
  if (!jurisdictionCountry) return false;
  return !!countries?.find((c) => c.id === jurisdictionCountry)?.regionRequired;
};

export type FeederSection = 'all' | 'numberUnits' | 'subscriptionOptions';

export const isFeederCompleteForStep = (
  feeder: Feeder,
  currentStep: number,
  stepForms: StepForms,
  section: FeederSection = 'all',
): boolean => {
  if (currentStep === 1) return isFeederComplete(feeder);
  if (currentStep === 3) {
    const eligibilityOk =
      (feeder.investorEligibility.allowed?.length ?? 0) +
        (feeder.investorEligibility.partiallyRestricted?.length ?? 0) >
      0;
    const icaOk = !isFeederUsApplicable(feeder) || Boolean(feeder.investmentCompaniesActStatus);
    return eligibilityOk && icaOk;
  }
  if (currentStep === 4) {
    return Boolean(
      feeder.offeringDocuments.notes?.trim() &&
        (feeder.offeringDocuments.documentIds?.length ?? 0) > 0 &&
        feeder.subscriptionDocuments.notes?.trim() &&
        (feeder.subscriptionDocuments.documentIds?.length ?? 0) > 0 &&
        feeder.riskDisclosure.notes?.trim() &&
        (feeder.riskDisclosure.documentIds?.length ?? 0) > 0,
    );
  }
  if (currentStep === 6) {
    const isSMA = stepForms.investmentDescription?.category === 'MAS';
    const sharesOk = isSMA ? true : validateShareClasses(feeder.numberUnits.shareClasses);
    const subscriptionOk = validateSubscriptionOpts(feeder.subscriptionOptions);
    if (section === 'numberUnits') return sharesOk;
    if (section === 'subscriptionOptions') return subscriptionOk;
    return sharesOk && subscriptionOk;
  }
  return true;
};

export const validateStep = (
  currentStep: number,
  stepForms: StepForms,
  fundCategories?: FundCategory[],
  countries?: Country[],
): boolean => {
  const category = fundCategories?.find(
    (c) => c.code === stepForms.investmentDescription?.category,
  );

  if (currentStep === 1) {
    if (!stepForms.fundStructure) return false;
    const isMF = stepForms.fundStructure === FundStructure.MasterFeeder;
    const jurisdictionCountry = stepForms.investmentDescription?.jurisdictionCountry?.trim();
    const shouldRequireJurisdictionState = isJurisdictionRegionRequired(
      jurisdictionCountry,
      countries,
    );
    const website = String(stepForms.investmentDescription?.website ?? '');

    // Master-Feeder: master-level legalStructure/jurisdiction live on feeders.
    const masterLegalJurisdictionOk = isMF
      ? true
      : Boolean(
          stepForms.investmentDescription.legalStructure?.trim() &&
            jurisdictionCountry &&
            (shouldRequireJurisdictionState
              ? stepForms.investmentDescription?.jurisdictionState?.trim()
              : true),
        );

    const feedersOk = isMF
      ? stepForms.feeders.length >= 2 && stepForms.feeders.every(isFeederComplete)
      : true;

    // Feeder names must be unique within the fund (case-insensitive, trimmed).
    const feederNamesUnique = isMF
      ? new Set(stepForms.feeders.map((f) => (f.name ?? '').trim().toLowerCase())).size ===
        stepForms.feeders.length
      : true;

    return Boolean(
      // investmentDescription
      stepForms.investmentDescription?.name?.trim() &&
        isValidHttpUrl(website) &&
        stepForms.investmentDescription?.category?.trim() &&
        masterLegalJurisdictionOk &&
        feedersOk &&
        feederNamesUnique &&
        ((category?.assets?.length ?? 0 > 0)
          ? (stepForms.investmentDescription?.assets?.length ?? 0 > 0)
          : true) &&
        ((category?.stages?.length ?? 0 > 0)
          ? (stepForms.investmentDescription?.stage?.length ?? 0 > 0)
          : true) &&
        ((category?.strategies?.length ?? 0 > 0)
          ? (stepForms.investmentDescription?.strategies?.length ?? 0 > 0)
          : true) &&
        (stepForms.investmentDescription?.description?.trim()?.length ?? 0) >= 10 &&
        // duration.startDate
        (stepForms.duration.isNewFund || stepForms.duration.startDate?.trim()) &&
        // duration.durationInYears
        (stepForms.duration.isOpenEnded || stepForms.duration.durationInYears) &&
        // redemption
        (stepForms.redemption.noRedemptionAllowed ||
          ((stepForms.redemption.lockUpPeriod?.length ?? 0) > 0 &&
            stepForms.redemption.frequency?.trim() &&
            stepForms.redemption.priority?.trim() &&
            ((stepForms.redemption.max !== null && stepForms.redemption.max > 0) ||
            (stepForms.redemption.gates && stepForms.redemption.gates?.toString().trim()) ||
            stepForms.redemption.months !== null
              ? stepForms.redemption.max !== null &&
                stepForms.redemption.max > 0 &&
                !!(stepForms.redemption.gates && stepForms.redemption.gates?.toString().trim()) &&
                stepForms.redemption.months !== null
              : true))) &&
        stepForms.accountingType !== null &&
        stepForms.returnProfile.feeType !== null,
    );
  }

  if (currentStep === 2) {
    return Boolean(
      // feesData.fees
      stepForms.feesData.fees?.every((line) =>
        Boolean(
          line.feeType?.trim() &&
            line.percentage !== undefined &&
            line.percentage !== null &&
            line.type?.trim() &&
            line.frequency?.trim(),
        ),
      ) &&
        // minInvestmentAmounts.currency
        stepForms.minInvestmentAmounts.currency?.trim() &&
        // minInvestmentAmounts.lines
        stepForms.minInvestmentAmounts.lines?.every((line) =>
          line.notApplicable
            ? true
            : line.value !== null && Number(line.value) > 0 && line.description?.trim(),
        ) &&
        (stepForms.distributionPeriod.frequencyNotApplicable ||
          (stepForms.distributionPeriod.period?.trim() &&
            stepForms.distributionPeriod.type?.trim() &&
            stepForms.distributionPeriod.frequency)) &&
        (stepForms.reportingPeriod.reportingNotApplicable ||
          stepForms.reportingPeriod.period?.trim()),
    );
  }

  if (currentStep === 3) {
    const isMF = stepForms.fundStructure === FundStructure.MasterFeeder;
    if (!isMF) {
      if (isFundUsApplicable(stepForms) && !stepForms.investmentCompaniesActStatus) return false;
      return true;
    }
    if ((stepForms.unassignedCountries?.length ?? 0) > 0) return false;
    return stepForms.feeders.every(
      (f) =>
        (f.investorEligibility.allowed?.length ?? 0) +
          (f.investorEligibility.partiallyRestricted?.length ?? 0) >
          0 &&
        (!isFeederUsApplicable(f) || Boolean(f.investmentCompaniesActStatus)),
    );
  }

  if (currentStep === 4) {
    if (stepForms.fundStructure === FundStructure.ManagedAccountSma) return true;
    const isMF = stepForms.fundStructure === FundStructure.MasterFeeder;
    if (isMF) {
      return stepForms.feeders.every((f) =>
        Boolean(
          f.offeringDocuments.notes?.trim() &&
            (f.offeringDocuments.documentIds?.length ?? 0) > 0 &&
            f.subscriptionDocuments.notes?.trim() &&
            (f.subscriptionDocuments.documentIds?.length ?? 0) > 0 &&
            f.riskDisclosure.notes?.trim() &&
            (f.riskDisclosure.documentIds?.length ?? 0) > 0,
        ),
      );
    }
    return Boolean(
      stepForms.offeringDocuments?.notes?.trim() &&
        stepForms.subscriptionDocuments?.notes?.trim() &&
        stepForms.riskDisclosure?.notes?.trim() &&
        (stepForms.offeringDocuments?.documentIds?.length ?? 0) > 0 &&
        (stepForms.subscriptionDocuments?.documentIds?.length ?? 0) > 0 &&
        (stepForms.riskDisclosure?.documentIds?.length ?? 0) > 0,
    );
  }

  if (currentStep === 5) {
    const faqItems = stepForms.faq ?? [];
    const idealProfile = stepForms.fundTargetInvestor?.idealInvestorProfile ?? [];
    const whoNotFor = stepForms.fundTargetInvestor?.whoItsNotFor ?? [];

    return Boolean(
      // howFundWorks
      stepForms.howFundWorks?.whatItInvestsIn?.trim() &&
        stepForms.howFundWorks?.howItAimsToMakeMoney?.trim() &&
        stepForms.howFundWorks?.whatCanGoWrong?.trim() &&
        // fundBenefits
        (stepForms.fundBenefits ?? []).every((b) => b.title?.trim() && b.description?.trim()) &&
        // fundTargetInvestor
        idealProfile.length > 0 &&
        idealProfile.every((item) => item.trim()) &&
        whoNotFor.length > 0 &&
        whoNotFor.every((item) => item.trim()) &&
        // faq (must have at least one item, each must have question + answer)
        faqItems.length > 0 &&
        faqItems.every((item) => item.question?.trim() && item.answer?.trim()),
    );
  }

  if (currentStep === 6) {
    const isSMA = category?.code === 'MAS';
    const isMF = stepForms.fundStructure === FundStructure.MasterFeeder;

    const numberUnitsValid = isSMA
      ? true
      : isMF
        ? stepForms.feeders.every((f) => validateShareClasses(f.numberUnits.shareClasses))
        : validateShareClasses(stepForms.numberUnits.shareClasses);

    const subscriptionOptionsValid = isMF
      ? stepForms.feeders.every((f) => validateSubscriptionOpts(f.subscriptionOptions))
      : validateSubscriptionOpts(stepForms.subscriptionOptions);

    return Boolean(
      stepForms.investmentAvailabilityStatus?.trim() &&
        numberUnitsValid &&
        subscriptionOptionsValid &&
        stepForms.transferability !== null &&
        stepForms.transferability !== undefined,
    );
  }

  return true;
};

export const getStepFieldErrors = (
  currentStep: number,
  stepForms: StepForms,
  countries?: Country[],
  feeder?: Feeder | null,
) => {
  const errors: Record<string, boolean> = {};
  const offeringDocs = feeder ? feeder.offeringDocuments : stepForms.offeringDocuments;
  const subscriptionDocs = feeder ? feeder.subscriptionDocuments : stepForms.subscriptionDocuments;
  const riskDisclosure = feeder ? feeder.riskDisclosure : stepForms.riskDisclosure;
  const subscriptionOptions = feeder ? feeder.subscriptionOptions : stepForms.subscriptionOptions;
  const numberUnits = feeder ? feeder.numberUnits : stepForms.numberUnits;

  if (currentStep === 1) {
    const jurisdictionCountry = stepForms.investmentDescription?.jurisdictionCountry?.trim();
    errors.name = !stepForms.investmentDescription?.name?.trim();
    errors.website = !isValidHttpUrl(String(stepForms.investmentDescription?.website ?? ''));
    errors.category = !stepForms.investmentDescription?.category?.trim();
    errors.legalStructure = !stepForms.investmentDescription?.legalStructure?.trim();
    errors.jurisdictionCountry = !jurisdictionCountry;
    errors.jurisdictionState =
      isJurisdictionRegionRequired(jurisdictionCountry, countries) &&
      !stepForms.investmentDescription.jurisdictionState?.trim();
    errors.strategies = !stepForms.investmentDescription?.strategies?.length;
    errors.assets = !stepForms.investmentDescription?.assets?.length;
    errors.stage = !stepForms.investmentDescription?.stage?.length;
    const descriptionLength = stepForms.investmentDescription?.description?.trim()?.length ?? 0;
    errors.description = descriptionLength === 0 || descriptionLength < 10;
    errors.day = !stepForms.duration.isNewFund && !stepForms.duration.startDate?.trim();
    errors.month = !stepForms.duration.isNewFund && !stepForms.duration.startDate?.trim();
    errors.year = !stepForms.duration.isNewFund && !stepForms.duration.startDate?.trim();
    errors.durationInYears = !stepForms.duration.isOpenEnded && !stepForms.duration.durationInYears;
    errors.lockUpPeriod =
      !stepForms.redemption.noRedemptionAllowed && !stepForms.redemption.lockUpPeriod?.length;
    errors.frequency =
      !stepForms.redemption.noRedemptionAllowed && !stepForms.redemption.frequency?.trim();
    errors.priority =
      !stepForms.redemption.noRedemptionAllowed && !stepForms.redemption.priority?.trim();
    const anyGateFieldFilled = Boolean(
      (stepForms.redemption.max !== null && stepForms.redemption.max > 0) ||
        (stepForms.redemption.gates && stepForms.redemption.gates?.toString().trim()) ||
        stepForms.redemption.months !== null,
    );
    errors.max =
      !stepForms.redemption.noRedemptionAllowed && anyGateFieldFilled
        ? stepForms.redemption.max === null
        : false;
    errors.gates =
      !stepForms.redemption.noRedemptionAllowed && anyGateFieldFilled
        ? !(stepForms.redemption.gates && stepForms.redemption.gates?.toString().trim())
        : false;
    errors.months =
      !stepForms.redemption.noRedemptionAllowed && anyGateFieldFilled
        ? stepForms.redemption.months === null
        : false;
    errors.feeType = !stepForms.returnProfile.feeType;
    errors.accountingType = !stepForms.accountingType;
  }

  if (currentStep === 2) {
    errors.feeType = !stepForms.feesData.fees?.every((line) => line?.feeType?.trim());
    errors.percentage = !stepForms.feesData.fees?.every((line) => line?.percentage);
    errors.type = !stepForms.feesData.fees?.every((line) => line?.type?.trim());
    errors.frequency = !stepForms.feesData.fees?.every((line) => line?.frequency?.trim());
    errors.currency = !stepForms.minInvestmentAmounts.currency?.trim();
    errors.value = !stepForms.minInvestmentAmounts.lines?.every((line) => {
      if (line.notApplicable) return true;
      return line.value !== null && Number(line.value) > 0;
    });
    errors.description = !stepForms.minInvestmentAmounts.lines?.every((line) => {
      if (line.notApplicable) return true;
      return line.description?.trim();
    });
    errors.distributionPeriod =
      !stepForms.distributionPeriod.period?.trim() &&
      !stepForms.distributionPeriod.frequencyNotApplicable;
    errors.distributionType =
      !stepForms.distributionPeriod.type?.trim() &&
      !stepForms.distributionPeriod.frequencyNotApplicable;
    errors.distributionFrequency =
      !stepForms.distributionPeriod.frequency &&
      !stepForms.distributionPeriod.frequencyNotApplicable;

    errors.reportingPeriod =
      !stepForms.reportingPeriod.period?.trim() &&
      !stepForms.reportingPeriod.reportingNotApplicable;
  }

  if (currentStep === 4 && stepForms.fundStructure !== FundStructure.ManagedAccountSma) {
    errors.offeringDocuments = !offeringDocs?.notes?.trim();
    errors.subscriptionDocuments = !subscriptionDocs?.notes?.trim();
    errors.riskDisclosure = !riskDisclosure?.notes?.trim();
    errors.offeringDocumentsFiles = !offeringDocs?.documentIds?.length;
    errors.subscriptionDocumentsFiles = !subscriptionDocs?.documentIds?.length;
    errors.riskDisclosureFiles = !riskDisclosure?.documentIds?.length;
  }

  if (currentStep === 5) {
    // howFundWorks
    errors.whatItInvestsIn = !stepForms.howFundWorks?.whatItInvestsIn?.trim();
    errors.howItAimsToMakeMoney = !stepForms.howFundWorks?.howItAimsToMakeMoney?.trim();
    errors.whatCanGoWrong = !stepForms.howFundWorks?.whatCanGoWrong?.trim();

    // fundBenefits
    (stepForms.fundBenefits ?? []).forEach((b, idx) => {
      if (!b.title?.trim()) errors[`fundBenefits.${idx}.title`] = true;
      if (!b.description?.trim()) errors[`fundBenefits.${idx}.description`] = true;
    });

    // fundTargetInvestor
    const idealProfile = stepForms.fundTargetInvestor?.idealInvestorProfile ?? [];
    const whoNotFor = stepForms.fundTargetInvestor?.whoItsNotFor ?? [];

    if (idealProfile.length === 0) {
      errors['fundTargetInvestor.idealInvestorProfile'] = true;
    }
    idealProfile.forEach((item, idx) => {
      if (!item.trim()) errors[`fundTargetInvestor.idealInvestorProfile.${idx}`] = true;
    });

    if (whoNotFor.length === 0) {
      errors['fundTargetInvestor.whoItsNotFor'] = true;
    }
    whoNotFor.forEach((item, idx) => {
      if (!item.trim()) errors[`fundTargetInvestor.whoItsNotFor.${idx}`] = true;
    });

    // faq (must have at least one item)
    const faqItems = stepForms.faq ?? [];
    if (faqItems.length === 0) {
      errors['faq.empty'] = true;
    }
    faqItems.forEach((item, idx) => {
      if (!item.question?.trim()) errors[`faq.${idx}.question`] = true;
      if (!item.answer?.trim()) errors[`faq.${idx}.answer`] = true;
    });
  }

  if (currentStep === 6) {
    const isSMA = stepForms.investmentDescription?.category === 'MAS';
    errors.investmentAvailabilityStatus = !stepForms.investmentAvailabilityStatus?.trim();

    if (!isSMA) {
      const shareClasses = numberUnits?.shareClasses ?? [];
      errors.className =
        shareClasses.length === 0 || !shareClasses.every((sc) => sc.className?.trim());
      errors.shares =
        shareClasses.length === 0 ||
        !shareClasses.every((sc) => sc.shares != null && sc.shares > 0);

      shareClasses.forEach((sc, cIdx) => {
        if (!sc.className?.trim()) errors[`shareClass.${cIdx}.class`] = true;
        if (sc.shares == null || sc.shares <= 0) errors[`shareClass.${cIdx}.numberOfShares`] = true;

        const shareholders = sc.shareholders ?? [];

        shareholders.forEach((sh, sIdx) => {
          const base = `shareholders.${cIdx}.${sIdx}`;

          if (!sh.type?.trim()) errors[`${base}.type`] = true;
          if (!sh.fullName?.trim()) errors[`${base}.fullName`] = true;

          const email = sh.email?.trim() ?? '';
          if (!email || !EMAIL_REGEX.test(email)) errors[`${base}.email`] = true;

          if (sh.numberOfShares == null || sh.numberOfShares <= 0) {
            errors[`${base}.numberOfShares`] = true;
          }
        });

        const total = sc.shares ?? 0;
        const allocated = shareholders.reduce((acc, sh) => acc + (sh.numberOfShares ?? 0), 0);

        if (total > 0 && allocated > total) {
          errors[`shareClass.${cIdx}.numberOfShares`] = true;
        }
      });

      errors.fullName =
        shareClasses.length === 0 ||
        !shareClasses.every((sc) => sc.shareholders.every((sh) => Boolean(sh.fullName?.trim())));

      errors.email =
        shareClasses.length === 0 ||
        !shareClasses.every((sc) =>
          sc.shareholders.every((sh) => {
            const email = sh.email?.trim() ?? '';
            return Boolean(email) && EMAIL_REGEX.test(email);
          }),
        );

      errors.numberOfShares =
        shareClasses.length === 0 ||
        !shareClasses.every((sc) =>
          sc.shareholders.every((sh) => sh.numberOfShares != null && sh.numberOfShares > 0),
        );

      errors.type =
        shareClasses.length === 0 ||
        !shareClasses.every((sc) => sc.shareholders.every((sh) => Boolean(sh.type?.trim())));
    }

    errors.subscriptionMethod = !subscriptionOptions?.subscriptionMethod;

    errors.bankDetails = !subscriptionOptions?.wireInformation?.bankDetails?.name?.trim();
    errors.bankDetailsAddress = !subscriptionOptions?.wireInformation?.bankDetails?.address?.trim();
    errors.bankDetailsCity = !subscriptionOptions?.wireInformation?.bankDetails?.city?.trim();
    errors.bankDetailsCountry = !subscriptionOptions?.wireInformation?.bankDetails?.country?.trim();
    errors.bankDetailsState = !subscriptionOptions?.wireInformation?.bankDetails?.state?.trim();
    errors.bankDetailsZipCode = !subscriptionOptions?.wireInformation?.bankDetails?.zipCode?.trim();
    errors.beneficiaryDetails =
      !subscriptionOptions?.wireInformation?.beneficiaryDetails?.name?.trim();
    errors.beneficiaryDetailsAddress =
      !subscriptionOptions?.wireInformation?.beneficiaryDetails?.address?.trim();
    errors.beneficiaryDetailsAccountNumber =
      !subscriptionOptions?.wireInformation?.beneficiaryDetails?.accountNumber?.trim();
    errors.beneficiaryDetailsBankIdentifierCode =
      !subscriptionOptions?.wireInformation?.beneficiaryDetails?.bankIdentifierCode?.trim();
    errors.beneficiaryDetailsCity =
      !subscriptionOptions?.wireInformation?.beneficiaryDetails?.city?.trim();
    errors.beneficiaryDetailsCountry =
      !subscriptionOptions?.wireInformation?.beneficiaryDetails?.country?.trim();
    errors.beneficiaryDetailsState =
      !subscriptionOptions?.wireInformation?.beneficiaryDetails?.state?.trim();
    errors.beneficiaryDetailsABARouting =
      !subscriptionOptions?.wireInformation?.beneficiaryDetails?.ABARouting?.trim();
    errors.beneficiaryDetailsZipCode =
      !subscriptionOptions?.wireInformation?.beneficiaryDetails?.zipCode?.trim();
    errors.transferability = !stepForms.transferability;
  }

  return errors;
};
